import Image from '@base/FutureImage';
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import Carousel from 'aquilla/core/molecules/Carousel';
import { useMediaQuery } from '@material-ui/core';
import GridContainer from './CommonComponents';

const StyGridContainer = styled(GridContainer)`
  margin-bottom: ${Spacings.SPACING_16B};
  margin-top: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
    padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_0B};
  }
`;

const StyCarousel = styled(Carousel)`
  & .aquilla-carousel-wrapper {
    ${mediaQuery.uptoMobile} {
      padding-left: ${Spacings.SPACING_4B};
      div {
        width: 320px;
        height: 220px;
        margin-right: ${Spacings.SPACING_3B};
      }
      div:last-child {
        width: 320px;
        height: 220px;
        margin-right: ${Spacings.SPACING_4B} !important;
      }
    }
  }
`;
const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacings.SPACING_7B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_4B};
    img {
      top: ${Spacings.SPACING_5} !important;
    }
  }
`;

const SecondHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  margin-top: ${Spacings.SPACING_20B};
  margin-bottom: ${Spacings.SPACING_11B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_4B};
    img {
      top: ${Spacings.SPACING_5} !important;
    }
  }
`;

const HeaderText = styled(Typography)`
  padding: 0 ${Spacings.SPACING_6B};
  background: var(--color-base-1);
  z-index: 1;

  ${mediaQuery.uptoTablet} {
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const ScholarShipData = styled.div`
  margin-top: ${Spacings.SPACING_5B};
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_1B};
`;

const Stats = styled(Typography)`
  margin-left: ${Spacings.SPACING_2B};
`;

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_20B};
  height: ${Spacings.SPACING_8B};
`;

const DiamondImageWrapper = styled.div`
  width: 25%;
  position: relative;
`;
const AnimationFallBackImage = ({ imageURL }) => {
  return <Image src={imageURL} width={80} height={80} alt="doubt-state-card" />;
};

export const Header = ({ text, isMobile }) => {
  return (
    <HeaderContainer>
      {!isMobile && (
        <DiamondImageWrapper>
          <Image
            src="/offline-centres/achievers-arrow-left.svg"
            layout="fill"
            alt="star"
          />
        </DiamondImageWrapper>
      )}
      <HeaderText variant="h2">{text}</HeaderText>
      {!isMobile && (
        <DiamondImageWrapper>
          <Image
            src="/offline-centres/achievers-arrow-right.svg"
            layout="fill"
            alt="star"
          />
        </DiamondImageWrapper>
      )}
    </HeaderContainer>
  );
};

const loadingStateAnimation = (
  <AnimationContainer
    Fallback={
      <AnimationFallBackImage imageURL="offline-centres/membersAttempting.svg" />
    }
    src={getAnimationPath('offlineCentres/JoiningMembers.json')}
  />
);

export const SecondHeader = ({ text, subText }) => {
  return (
    <SecondHeaderContainer>
      <Typography variant="h2">{text}</Typography>
      <ScholarShipData>
        {loadingStateAnimation}
        <Stats variant="h5">{subText}</Stats>
      </ScholarShipData>
    </SecondHeaderContainer>
  );
};
const Container = styled.div`
  grid-column: span 24;

  ${mediaQuery.uptoTablet} {
    margin: 0;
    width: 100%;
  }
`;

const Section = styled.div`
  margin-top: ${Spacings.SPACING_3B};
  display: flex;
  gap: ${Spacings.SPACING_4B};
  overflow-x: clip;
`;

const StyImage = styled(Image)`
  border-radius: ${Spacings.SPACING_2B};
  object-fit: cover;
  ${useMediaQuery.uptoMobile} {
    width: 320px;
    height: 220px;
    object-fit: contain;
  }
`;
const Acheivers = ({ data }) => {
  const { isMobile, isTablet } = useWindowSize();
  const {
    webHeader,
    webHeader2,
    appHeader,
    socialProofingText,
    imagesWeb: images,
    imagesWeb2: images2,
    images: mWebImages
  } = data;

  const renderWebView = () => {
    // change image to image map
    return (
      <>
        {!isTablet && (
          <>
            <Header text={webHeader} isMobile={isMobile} />
            <Section>
              {images.map((imgSrc, i) => (
                <StyImage
                  src={imgSrc}
                  width={560}
                  height={350}
                  key={`carousel-${imgSrc}`}
                  alt={`img-${i}`}
                  // objectFit="cover"
                />
              ))}
            </Section>
          </>
        )}

        <SecondHeader text={webHeader2} subText={socialProofingText} />
        <Section>
          <StyCarousel
            itemsPerSlide={2}
            variant="multiple"
            mode="auto"
            controls={{ showArrows: true }}
          >
            {images2?.map((imgSrc, i) => (
              // <div style={{ width: '704px', height: '350px' }}> Something </div>
              <StyImage
                src={imgSrc}
                width={560}
                height={350}
                key={`carousel-${imgSrc}`}
                alt={`img-${i}`}
              />
            ))}
          </StyCarousel>
        </Section>
      </>
    );
  };

  const rendermWebView = () => {
    return (
      <>
        <HeaderText variant="h4">{appHeader}</HeaderText>
        <Section>
          <StyCarousel
            itemsPerSlide={1}
            variant="multiple"
            mode="auto"
            controls={{ showArrows: true }}
          >
            {mWebImages.map((imgSrc, i) => (
              <StyImage
                src={imgSrc}
                key={`carousel-${imgSrc}`}
                alt={`img-${i}`}
                width={320}
                height={220}
              />
            ))}
          </StyCarousel>
        </Section>
      </>
    );
  };
  return (
    <StyGridContainer>
      <Container> {isMobile ? rendermWebView() : renderWebView()}</Container>
    </StyGridContainer>
  );
};

export default Acheivers;
